'use client';

import { EPlatform } from '@/utils/enums/platform-enum';
import type { CampaignsServerData } from '../../types';
import CollaborationBanner from './components/CollaborationBanner';
import CollaborationMedia from './components/CollaborationMedia';
import CollaborationProductList from './components/CollaborationProductList';
import CollaborationSeries from './components/CollaborationSeries';
import styles from './CollaborationPage.module.css';
import { useCollaborationTracking } from './useCollaborationTracking';

export interface CollaborationPageProps {
  serverData: CampaignsServerData;
}

export default function CollaborationPage({ serverData }: CollaborationPageProps) {
  const data = serverData.runtimeConfig.collaboration_data;
  const tracking = useCollaborationTracking(serverData.runtimeConfig);
  if (!data) return null;
  const isWeb = serverData.deviceType === EPlatform.WEB;

  return (
    <main
      className={styles.page}
      data-testid="collaboration-page"
      onVolumeChangeCapture={(event) => {
        const activeVideo = event.target;
        if (!(activeVideo instanceof HTMLVideoElement) || activeVideo.muted) return;
        event.currentTarget.querySelectorAll('video').forEach((video) => {
          if (video !== activeVideo && !video.muted) video.muted = true;
        });
      }}
    >
      <CollaborationBanner
        data={data.banner}
        isWeb={isWeb}
        onExposure={tracking.trackBannerExposure}
        onButtonClick={tracking.trackBannerClick}
      />
      <CollaborationSeries
        data={data.series}
        isWeb={isWeb}
        onNavClick={tracking.trackSeriesNavClick}
        onNavExposure={tracking.trackSeriesNavExposure}
        onProductClick={tracking.trackSeriesProductClick}
        onProductExposure={tracking.trackSeriesProductExposure}
        onProductCollect={tracking.trackSeriesProductCollect}
      />
      <CollaborationMedia
        data={data.media}
        isWeb={isWeb}
        onProductCollect={tracking.trackGalleryProductCollect}
        onMediaExposure={tracking.trackMediaExposure}
        onMediaClick={tracking.trackMediaClick}
      />
      <CollaborationProductList
        data={data.productList}
        initialResponse={serverData.collaborationProductList}
        isWeb={isWeb}
        returnStateKey={`${serverData.runtimeConfig.slug || 'default'}-${isWeb ? 'web' : 'wap'}`}
        onProductClick={tracking.trackProductListClick}
        onProductExposure={tracking.trackProductListExposure}
        onProductCollect={tracking.trackProductListCollect}
      />
    </main>
  );
}
